import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ConsoleLotteryCsvQuery,
  ConsoleLotteryCsvQueryVariables,
  ConsolePreSaleTitleFragmentDoc,
  ExportPreSaleApplicantsFragmentDoc,
  UpdatePreSaleLotteryResultFromCsvMutation,
  UpdatePreSaleLotteryResultFromCsvMutationVariables,
} from '@/clients/utoniq-core/schema'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { parseCsvFromUrl } from '@/lib/papaparse'
import { getResource, OtherType } from '@/lib/resourceUpload'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

type RawCSVData = {
  preSaleId: string
  userId: string
  fullName: string
  gender: string
  birthday: string
  prefecture: string
  address: string
  membershipPlan: string
  choice1: string
  choice2: string
  choice3: string
  purchaseCountChoice1: string
  purchaseCountChoice2: string
  purchaseCountChoice3: string
  paymentMethod: string
  status: string
  appliedAt: string
  win: string
}

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { t: tCommon } = useTranslation()
  const { t } = useTranslation('consoleLotteryCsvUpload')
  const { showError, showMessage } = useMessage()
  const { upload, progress } = useUserTmpUploader(user?.uid || '')
  const [csvData, setCsvData] = useState<State['csvData']>([])
  const { router, routerPages, generatePath } =
    useRouter<{ preSaleId: string }>()
  const [csvPath, setCsvPath] = useState<string>('')

  const { data } = useQuery<
    ConsoleLotteryCsvQuery,
    ConsoleLotteryCsvQueryVariables
  >(QUERY, {
    variables: {
      preSaleId: router.query.preSaleId,
      preSaleIdString: router.query.preSaleId,
    },
  })
  const [updatePreSaleLotteryResultFromCsv, { loading: submitting }] =
    useMutation<
      UpdatePreSaleLotteryResultFromCsvMutation,
      UpdatePreSaleLotteryResultFromCsvMutationVariables
    >(UPDATE_PRESALE_LOTTERY_RESULT_FROM_CSV)

  return {
    state: {
      artistId: user?.uid ?? '',
      csvData,
      progress,
      applicantsCsvUrl: data?.exportPreSaleApplicants.csvUrl ?? null,
      preSale: data?.artistPreSale ?? null,
      submitting,
    },
    actions: {
      async onDrop() {
        const file = await getResource(OtherType.Csv).catch(error => {
          showError(new Error(tCommon([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, true, true)
        const uuid = createUuid()

        const rawCsvData = await parseCsvFromUrl<RawCSVData>(fileUrl)

        // CSVのうち、当選しているユーザーのみでフィルタする
        const winnerData = rawCsvData.data
          .filter(value => !!value.win)
          .map(value => {
            // 当選したアイテムIDを取得
            const win = value.win as '1' | '2' | '3'
            const winItemId = value[`choice${win}` as 'choice1']
            const item = data?.artistPreSale.items.find(
              item => item.id === winItemId
            )
            const purchaseCount =
              value[`purchaseCountChoice${win}` as 'purchaseCountChoice1']

            return {
              fullName: value.fullName,
              win: value.win,
              itemTitle: item?.title ?? '',
              purchaseCount,
              paymentMethod: value.paymentMethod,
              appliedAt: value.appliedAt,
            }
          })
        setCsvData(winnerData)
        setCsvPath(tmpPath)

        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onUpdateLotteryResultFromCsv() {
        await updatePreSaleLotteryResultFromCsv({
          variables: {
            inputs: {
              csvPath,
              preSaleId: router.query.preSaleId,
              resultNoticedAt: data?.artistPreSale.lottery?.resultNoticedAt,
            },
          },
        })
          .then(() => {
            showMessage(t('successUpdateLotteryResult'))
            router.goBack(
              generatePath(routerPages.ConsoleLotteryManagements, {
                preSaleId: router.query.preSaleId,
              })
            )
          })
          .catch(showError)
      },
      async onRemoveCsvFile() {
        setCsvData([])
      },
    },
  }
})

const QUERY = gql`
  query ConsoleLotteryCsv($preSaleId: ID!, $preSaleIdString: String!) {
    artistPreSale(id: $preSaleId) {
      ...ConsolePreSaleTitle
    }
    exportPreSaleApplicants(preSaleId: $preSaleIdString) {
      ...ExportPreSaleApplicants
    }
  }
  ${ExportPreSaleApplicantsFragmentDoc}
  ${ConsolePreSaleTitleFragmentDoc}
`

const UPDATE_PRESALE_LOTTERY_RESULT_FROM_CSV = gql`
  mutation updatePreSaleLotteryResultFromCsv(
    $inputs: UpdatePreSaleLotteryResultFromCsvInput!
  ) {
    updatePreSaleLotteryResultFromCsv(inputs: $inputs) {
      id
    }
  }
`
